/**
 * Route Guard Component
 * Protects routes based on user roles
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { Text, Card, Button } from "@fluentui/react-components";
import { ShieldError24Regular } from "@fluentui/react-icons";

import { usePermissions } from "./usePermissions";
import type { AppRole } from "./authConfig";

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
  requiredPermissions?: Array<{ toolId: string; permission: string }>;
  fallback?: React.ReactNode;
}

/**
 * Access Denied component
 */
const AccessDenied: React.FC<{ message?: string }> = ({ message }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      padding: "40px",
    }}
  >
    <Card style={{ maxWidth: "400px", textAlign: "center", padding: "32px" }}>
      <ShieldError24Regular
        style={{ fontSize: "48px", color: "#d13438", marginBottom: "16px" }}
      />
      <Text size={600} weight="semibold" block style={{ marginBottom: "8px" }}>
        Access Denied
      </Text>
      <Text size={400} style={{ color: "#605e5c", marginBottom: "24px" }}>
        {message || "You don't have permission to access this page."}
      </Text>
      <Button appearance="primary" as="a" href="/">
        Go to Home
      </Button>
    </Card>
  </div>
);

/**
 * AuthGuard - Protects routes based on roles and permissions
 *
 * @example
 * <AuthGuard requiredRoles={["Portal.Admin"]}>
 *   <AdminPage />
 * </AuthGuard>
 *
 * @example
 * <AuthGuard requiredPermissions={[{ toolId: "inventory", permission: "edit" }]}>
 *   <EditInventory />
 * </AuthGuard>
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRoles,
  requiredPermissions,
  fallback,
}) => {
  const { hasRole, hasPermission, isLoading } = usePermissions();

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <Text>Checking permissions...</Text>
      </div>
    );
  }

  // Check required roles (from Entra ID)
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => hasRole(role));
    if (!hasRequiredRole) {
      if (fallback) return <>{fallback}</>;
      return <AccessDenied message="You don't have the required role to access this page." />;
    }
  }

  // Check required permissions (from Permission Service)
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every(({ toolId, permission }) =>
      hasPermission(toolId, permission)
    );
    if (!hasAllPermissions) {
      if (fallback) return <>{fallback}</>;
      return <AccessDenied message="You don't have the required permissions for this action." />;
    }
  }

  return <>{children}</>;
};

export default AuthGuard;
